import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AwardCompliantBadge } from './TrustBadges';

const api = () => axios.create({
  baseURL: '/api',
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

/**
 * NextShiftCard
 * 
 * Retention loop component that shows:
 * - The most suitable upcoming shift for the worker
 * - Earnings milestone progress ("You've earned $X this week")
 * - Call-to-action to apply
 * 
 * Should be placed prominently on worker dashboard
 */
export default function NextShiftCard() {
  const [nextShift, setNextShift] = useState(null);
  const [weeklyEarnings, setWeeklyEarnings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchNextShiftAndEarnings();
  }, []);

  const fetchNextShiftAndEarnings = async () => {
    try {
      setLoading(true);
      const [shiftRes, earningsRes] = await Promise.all([
        api().get('/shifts/recommended-next?limit=1'),
        api().get('/workers/earnings/weekly-total')
      ]);
      
      if (shiftRes.data && shiftRes.data.length > 0) {
        setNextShift(shiftRes.data[0]);
      }
      
      if (earningsRes.data) {
        setWeeklyEarnings(parseFloat(earningsRes.data.total || 0) || 0);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load shift recommendations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-neutral-200 rounded-xl p-6 animate-pulse">
        <div className="h-4 bg-neutral-200 rounded w-1/3 mb-4" />
        <div className="h-20 bg-neutral-100 rounded" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-xs text-amber-700 font-medium">{error}</p>
      </div>
    );
  }

  if (!nextShift) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="text-3xl">🔍</div>
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-1">No shifts available right now</h3>
            <p className="text-sm text-blue-800 mb-3">New shifts post daily. Check back later or set your availability.</p>
            <button
              onClick={() => window.location.href = '/worker/availability'}
              className="text-sm font-semibold text-blue-600 hover:text-blue-700 underline"
            >
              Set Your Availability →
            </button>
          </div>
        </div>
      </div>
    );
  }

  const shiftDate = new Date(nextShift.date);
  const isToday = shiftDate.toDateString() === new Date().toDateString();
  const isTomorrow = shiftDate.toDateString() === new Date(Date.now() + 86400000).toDateString();
  
  const dateLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : shiftDate.toLocaleDateString('en-AU', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  });

  // Earnings milestones
  const milestones = [
    { target: 50, label: '$50 this week', icon: '🎯' },
    { target: 100, label: '$100 this week', icon: '⭐' },
    { target: 250, label: '$250 this week', icon: '🚀' },
  ];

  const nextMilestone = milestones.find(m => weeklyEarnings < m.target);

  return (
    <div className="space-y-4">
      {/* Earnings milestone */}
      {nextMilestone && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{nextMilestone.icon}</span>
              <div>
                <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide">Earnings Milestone</p>
                <p className="font-semibold text-amber-900">{nextMilestone.label}</p>
              </div>
            </div>
            <span className="text-sm font-bold text-amber-900">${parseFloat(weeklyEarnings || 0).toFixed(2)}</span>
          </div>
          <div className="w-full h-2 bg-amber-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-300"
              style={{ width: `${Math.min((weeklyEarnings / nextMilestone.target) * 100, 100)}%` }}
            />
          </div>
          <p className="text-xs text-amber-700 mt-2">
            ${(parseFloat(nextMilestone.target || 0) - parseFloat(weeklyEarnings || 0)).toFixed(2)} to go
          </p>
        </div>
      )}

      {/* Next shift card */}
      <div className="bg-white border-2 border-emerald-300 rounded-xl overflow-hidden hover:shadow-lg transition-all">
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 border-b border-emerald-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">Your Next Shift</p>
              <p className="text-sm font-bold text-emerald-900 mt-0.5">{dateLabel} • {nextShift.start_time || '9:00 AM'} - {nextShift.end_time || '5:00 PM'}</p>
            </div>
            <span className="text-3xl">⏰</span>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Shift details */}
          <div className="space-y-3">
            <div>
              <h3 className="font-bold text-neutral-900 text-lg">{nextShift.title}</h3>
              <p className="text-sm text-neutral-600 mt-1">{nextShift.company_name}</p>
            </div>

            <div className="flex items-center gap-2 text-sm text-neutral-700">
              <span className="text-base">📍</span>
              <span>{nextShift.location}</span>
            </div>

            {/* Pay info */}
            <div className="flex items-center justify-between py-3 bg-neutral-50 px-4 rounded-lg border border-neutral-200">
              <span className="text-sm font-semibold text-neutral-700">Pay</span>
              <span className="font-bold text-lg text-emerald-600">${nextShift.wage}/hr</span>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-2">
              <AwardCompliantBadge wage={nextShift.wage} />
              {nextShift.fair_work_verified && (
                <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                  🔒 Fair Work
                </div>
              )}
            </div>

            {nextShift.description && (
              <p className="text-sm text-neutral-600 line-clamp-2">{nextShift.description}</p>
            )}
          </div>

          {/* CTA buttons */}
          <div className="flex gap-2 pt-2 border-t border-neutral-100">
            <button
              onClick={() => window.location.href = `/shifts/${nextShift.id}`}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-all"
            >
              View Details
            </button>
            <button
              onClick={() => {
                // Trigger quick apply
                const event = new CustomEvent('quickApply', { detail: { shiftId: nextShift.id } });
                window.dispatchEvent(event);
              }}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-all"
            >
              Apply Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
